import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { PaginatorState } from 'primeng/paginator';
import { take } from 'rxjs';
import { AnimalModel } from 'src/app/modules/animals/models/animal.model';
import { AnimalOriginRepository } from 'src/app/modules/animals/states/animal-origin.repository';
import { AnimalWhereaboutsRepository } from 'src/app/modules/animals/states/animal-whereabouts.repository';
import { ContactRepository } from 'src/app/modules/contact/states/contact.repository';

export interface AnimalWhereaboutView {
  type?: string;
  location?: string;
  date?: string | Date | null;
  contact?: { id: number };
}

export interface AnimalOriginView {
  type?: string;
  location?: string;
  dateOfReceipt?: string | Date | null;
}

/**
 * Bereits bekannte Herkunft/Verbleib pro Tier-ID. Ist das gesetzt, laedt die
 * Komponente nichts nach (siehe loadOriginsForAnimals/loadWhereaboutsForAnimals).
 */
export interface AnimalListItemContext {
  origin?: AnimalOriginView;
  whereabout?: AnimalWhereaboutView;
}


@Component({
  selector: 'app-animal-list-item',
  standalone: false,
  templateUrl: './animal-list-item.component.html',
  styleUrl: './animal-list-item.component.scss',
})
export class AnimalListItemComponent {

  @Input() animals: AnimalModel[] = [];

  // Pagination-Konfiguration
  @Input() pageable = false;          // Pagination an/aus
  @Input() pageSize = 10;             // Items pro Seite
  @Input() totalRecords?: number;     // optional, für Server-Paging
  @Input() hideDetails = false;

  /** Vorgeladene Herkunft/Verbleib je Tier-ID. Unterdrueckt das Nachladen. */
  @Input() preloadedContext?: Map<number, AnimalListItemContext> | null;

  @Input() first: number | null = null;

  @Input() disableNavigation = false;

  @Output() animalSelected = new EventEmitter<AnimalModel>();

  @Output() pageChange = new EventEmitter<{ page: number; rows: number; first: number }>();

  currentPage = 0;
  whereabouts = new Map<number, AnimalWhereaboutView>();
  origins = new Map<number, AnimalOriginView>();

  whereaboutsLoading = new Set<number>();
  originsLoading = new Set<number>();

  constructor(
    private animalWhereaboutsRepository: AnimalWhereaboutsRepository,
    private animalOriginRepository: AnimalOriginRepository,
    private contactRepository: ContactRepository,
    private transloco: TranslocoService,
    private _router: Router
  ) { }

  ngOnInit(): void {
    if (this.animals && this.animals.length) {
      if (this.pageable && this.first != null) {
        this.currentPage = Math.floor(this.first / this.pageSize);
      }
      const animalsForPage = this.getAnimalsForCurrentPage();
      this.loadWhereaboutsForAnimals(animalsForPage);
      this.loadOriginsForAnimals(animalsForPage);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['animals'] || changes['preloadedContext']) {
      if (this.pageable && this.first != null) {
        this.currentPage = Math.floor(this.first / this.pageSize);
      } else {
        this.currentPage = 0;
      }
      this.whereabouts.clear();
      this.origins.clear();

      if (this.animals?.length) {
        const animalsForPage = this.getAnimalsForCurrentPage();
        this.loadWhereaboutsForAnimals(animalsForPage);
        this.loadOriginsForAnimals(animalsForPage);
      }

      if (changes['first'] && !changes['animals']) {
        if (this.pageable && this.first != null) {
          this.currentPage = Math.floor(this.first / this.pageSize);
          const animalsForPage = this.getAnimalsForCurrentPage();
          this.loadWhereaboutsForAnimals(animalsForPage);
          this.loadOriginsForAnimals(animalsForPage);
        }
      }
    }
  }

  onRowClick(animal: AnimalModel, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.disableNavigation) {
      // an Parent melden
      this.animalSelected.emit(animal);
    } else {
      // Standardverhalten: zu /tiere/:id navigieren
      this._router.navigate(['/tiere', animal.id]);
    }
  }

  getLastOldBookNumber(animal: AnimalModel): string | null {
    if (!animal.oldBookNumberList || animal.oldBookNumberList.length === 0) {
      return null;
    }
    return animal.oldBookNumberList[animal.oldBookNumberList.length - 1];
  }

  getDateFormat(): string {
    return 'dd.MM.yyyy';
  }

  formatDate(dateInput: string | Date | null): string {
    if (!dateInput) return '';

    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '';

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}.${month}.${year}`;
  }

  // Liste für *Anzeige* der Tiere
  get pagedAnimals(): AnimalModel[] {
    if (!this.pageable) {
      return this.animals;
    }
    return this.getAnimalsForCurrentPage();
  }

  private getAnimalsForCurrentPage(): AnimalModel[] {
    if (!this.pageable) {
      return this.animals;
    }

    const start = this.currentPage * this.pageSize;
    return this.animals.slice(start, start + this.pageSize);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = event.page ?? 0;
    this.pageSize = event.rows ?? this.pageSize;

    const animalsForPage = this.getAnimalsForCurrentPage();
    this.loadWhereaboutsForAnimals(animalsForPage);
    this.loadOriginsForAnimals(animalsForPage);

    const first = this.currentPage * this.pageSize;

    this.pageChange.emit({
      page: this.currentPage,
      rows: this.pageSize,
      first
    });
  }

loadWhereaboutsForAnimals(animals: AnimalModel[]): void {
  if (!animals || animals.length === 0) return;

  if (this.preloadedContext) {
    animals.forEach((animal) => {
      if (!animal?.id) return;
      this.whereabouts.set(animal.id, this.preloadedContext?.get(animal.id)?.whereabout ?? ({} as AnimalWhereaboutView));
    });
    return;
  }

  animals.forEach((animal) => {
    if (!animal?.id) return;

    // schon geladen oder gerade am Laden → nichts tun
    if (this.whereabouts.has(animal.id) || this.whereaboutsLoading.has(animal.id)) {
      return;
    }

    this.whereaboutsLoading.add(animal.id);

    this.animalWhereaboutsRepository.getByAnimalId(animal.id)
      .pipe(take(1))
      .subscribe({
        next: (whereabouts: any[]) => {
          if (Array.isArray(whereabouts) && whereabouts.length > 0) {
            const latestWhereabout = whereabouts
              .reduce((prev, current) => (prev.id > current.id ? prev : current));

            if (latestWhereabout.contact?.id) {
              this.contactRepository.getById(latestWhereabout.contact.id)
                .pipe(take(1))
                .subscribe({
                  next: (contact) => {
                    const location = contact?.city ?? latestWhereabout.location;
                    this.whereabouts.set(animal.id, {
                      ...latestWhereabout,
                      location,
                      contact: { id: latestWhereabout.contact.id }
                    });
                    this.whereaboutsLoading.delete(animal.id);
                  },
                  error: () => {
                    this.whereaboutsLoading.delete(animal.id);
                  }
                });
            } else {
              this.whereabouts.set(animal.id, latestWhereabout);
              this.whereaboutsLoading.delete(animal.id);
            }
          } else {
            // explizit "kein Eintrag"
            this.whereabouts.set(animal.id, {} as any);
            this.whereaboutsLoading.delete(animal.id);
          }
        },
        error: () => {
          this.whereaboutsLoading.delete(animal.id);
        }
      });
  });
}

loadOriginsForAnimals(animals: AnimalModel[]): void {
  if (!animals || animals.length === 0) return;

  if (this.preloadedContext) {
    animals.forEach((animal) => {
      if (!animal?.id) return;
      this.origins.set(animal.id, this.preloadedContext?.get(animal.id)?.origin ?? ({} as AnimalOriginView));
    });
    return;
  }

  animals.forEach((animal) => {
    if (!animal?.id) return;

    if (this.origins.has(animal.id) || this.originsLoading.has(animal.id)) {
      return;
    }

    this.originsLoading.add(animal.id);

    this.animalOriginRepository.getByAnimalId(animal.id)
      .pipe(take(1))
      .subscribe({
        next: (origins: any[]) => {
          if (Array.isArray(origins) && origins.length > 0) {
            const latestOrigin = origins
              .reduce((prev, curr) => (prev.id > curr.id ? prev : curr));

            this.origins.set(animal.id, {
              type: latestOrigin.type,
              location: latestOrigin.location,
              dateOfReceipt: latestOrigin.dateOfReceipt
            });
          } else {
            this.origins.set(animal.id, {} as any);
          }
          this.originsLoading.delete(animal.id);
        },
        error: () => {
          this.originsLoading.delete(animal.id);
        }
      });
  });
}

  getOrigin(animalId: number) {
    return this.origins.get(animalId) ?? null;
  }

  getWhereabout(animalId: number): AnimalWhereaboutView | null {
    return this.whereabouts.get(animalId) ?? null;
  }

  isWhereaboutLoading(animalId: number): boolean {
  return this.whereaboutsLoading.has(animalId);
}

isOriginLoading(animalId: number): boolean {
  return this.originsLoading.has(animalId);
}


  getBirthDisplay(animal: AnimalModel): { text: string; age: string | null } {
    // 1️⃣ birthday vorhanden
    if (animal.birthday) {
      const date = new Date(animal.birthday);
      const formatted = this.formatDate(date);
      const age = animal.age;
      return { text: formatted, age };
    }

    // 2️⃣ birthYear vorhanden → NUR Jahr anzeigen
    if (animal.birthYear) {
      const d = new Date(animal.birthYear);
      const year = d.getFullYear();
      const age = animal.age;
      const approx = this.transloco.translate('animalAdd.general.specifications.approx');
      return { text: `${approx} ${year}`, age };
    }

    // 3️⃣ nichts vorhanden
    return { text: '-', age: null };
  }
}
